import * as React from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import NextBreadcrumb from "@/components/breadcrumbs";

import { Sparkles, PanelRightClose, PanelRightOpen, Gem, Headset } from "lucide-react";
import { ConsultationCTA } from "@/components/cta-consultation";
import { UserContext } from "@/context/user";
import { UIContext } from "@/context/ui";
import { ProjectContext } from "@/context/project";
import { ConsultationContext } from "@/context/consultation";

import { ProjectSwitcher } from "./project-switcher";
import buzookaIcon from "@/assets/icons/buzooka-icon.svg";
import { Button } from "./ui/button";

export function AppHeader() {
  const navigate = useNavigate();
  const uiContext = React.useContext(UIContext);
  const { user } = React.useContext(UserContext);
  const { projectsMap } = React.useContext(ProjectContext);
  const consultationContext = React.useContext(ConsultationContext);
  const location = useLocation();
  const isProfilePage = location.pathname === "/profile";

  const projects = Object.values(projectsMap);

  if (!uiContext) {
    throw new Error("AppHeader must be used within a UIProvider");
  }

  const { rightSidebar, setRightSidebar, chatTitle } = uiContext;
  const isChatPage = location.pathname.startsWith("/chat");

  return (
    <header className="sticky top-0 flex h-16 w-full items-center gap-2 border-b bg-background px-4 z-50">
      <div className="flex flex-1 items-center">
        <Link to="/" className="flex items-center">
          <img src={buzookaIcon} alt="Buzooka" className="h-6 w-6" />
        </Link>

        {!!projects.length && <>
          <span className="text-muted-foreground/35 font-light px-4">/</span>
          <ProjectSwitcher projects={projects} hideLogo={true} />
        </>}

        {isProfilePage ? (
          <>
            <span className="text-muted-foreground/35 font-light px-4">/</span>
            <h1 className="text-sm font-medium">Profile</h1>
          </>
        ) : isChatPage ? (
          <>
            <span className="text-muted-foreground/35 font-light px-4">/</span>
            <Link to="/chat" className="text-sm font-medium hover:text-primary transition-colors">Chat</Link>
            {chatTitle && (
              <>
                <span className="text-muted-foreground/35 font-light px-4">/</span>
                <h1 className="text-sm font-medium truncate max-w-[200px]">{chatTitle}</h1>
              </>
            )}
          </>
        ) : location.pathname === "/" ? (
          null
        ) : location.pathname === "/new" ? (
          <>
            <span className="text-muted-foreground/35 font-light px-4">/</span>
            <h1 className="text-sm font-medium">New</h1>
          </>
        ) : (
          <>
            <span className="text-muted-foreground/35 font-light px-4">/</span>
            <NextBreadcrumb hideProject={true} />
          </>
        )}
      </div>
      <div className="flex items-center gap-2 ml-auto">

        {user?.subscribed && (
          <div className="flex items-center gap-2">
            <ConsultationCTA ctaLink="/consultation/hire">
              <Gem className="h-4 w-4" />
              Hire Dev
            </ConsultationCTA>
            {consultationContext?.showBanner && (
              <ConsultationCTA ctaLink="/consultation/schedule">
                <Headset className="h-4 w-4" />
                Free Consultation
              </ConsultationCTA>
            )}
          </div>
        )}

        {isChatPage ? (
          <Button
            onClick={() => setRightSidebar(!rightSidebar)}
            variant={'default'}
          >
            Chats
            {rightSidebar ? (
              <PanelRightClose className="h-4 w-4" />
            ) : (
              <PanelRightOpen className="h-4 w-4" />
            )}
          </Button>
        ) : (
          <Button
            variant={'secondary'}
            onClick={() => {
              navigate("/chat", { state: { openSidebar: true } });
            }}
          >
            Chats
            <Sparkles />
          </Button>
        )}
      </div>
    </header>
  );
}
